import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../context/NotificationContext';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import type { Integration } from '../../services/integration.types';
import PageLayout from '../common/PageLayout';
import IntegrationsOverview from './IntegrationsOverview';
import IntegrationSelectPage from './IntegrationSelectPage';
import IntegrationCreatePage from './IntegrationCreatePage';
import IntegrationDetailPage from './IntegrationDetailPage';
import { useIntegrationMutations } from './hooks/useIntegrationMutations';
import { useStepValidation } from '../../hooks/useStepValidation';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';
import Breadcrumb from '../common/Breadcrumb';
import useDashboardService from '../../services/dashboard.service';

const IntegrationsMain: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { integrations, isLoading, syncIntegration, isSyncing } = useIntegrationsStatusContext();
    const [deletingIntegrationName, setDeletingIntegrationName] = useState<string | null>(null);
    const [hasCheckedBalance, setHasCheckedBalance] = useState(false);
    const dashboardService = useDashboardService();

    const {
        deleteIntegration,
        isExchangeCreating,
        isWalletCreating,
        isCsvImporting,
    } = useIntegrationMutations();

    const { validateStep1ToStep2 } = useStepValidation();

    const handleSync = (integration: Integration) => {
        integration.status.status = 'in_progress';
        syncIntegration(
            { 
                jobId: integration.jobId, 
                platformName: integration.platformName,
                name: integration.name
            },
            {
                onSuccess: () => {},
                onError: () => {
                    showNotification(t('integrations.overview.notifications.syncError'), 'error');
                },
            }
        );
    };

    // Check if user has balance data in Firebase (user_livebalances_ct) - if not, trigger first sync
    useEffect(() => {
        const checkBalanceAndSync = async () => {
            if (hasCheckedBalance || !integrations || integrations.length === 0 || isLoading) {
                return;
            }

            try {
                const hasBalance = await dashboardService.hasLiveBalance();
                
                // If no balance data exists, trigger sync
                if (!hasBalance) {
                    setHasCheckedBalance(true);
                    
                    console.log('[Integrations] No live balance data found - triggering first sync for all integrations');
                    
                    // Sync all integrations (same as manual sync)
                    integrations.forEach((integration) => {
                        if (integration.jobId) {
                            handleSync(integration);
                        }
                    });
                } else {
                    // Balance data exists, mark as checked
                    console.log('[Integrations] Live balance data exists - skipping first sync');
                    setHasCheckedBalance(true);
                }
            } catch (error) {
                console.error('Error checking balance data:', error);
                // Mark as checked to avoid repeated attempts
                setHasCheckedBalance(true);
            }
        };

        checkBalanceAndSync();
    }, [hasCheckedBalance, integrations, isLoading]);

    const handleDelete = async (integration: Integration) => {
        setDeletingIntegrationName(integration.name);
        
        const optimisticUpdate = setTimeout(() => {}, 500);
        
        deleteIntegration(
            {
                name: integration.name,
                jobId: integration.jobId,
                jobTableName: integration.jobTableName,
            },
            {
                onSuccess: () => {
                    clearTimeout(optimisticUpdate);
                    setDeletingIntegrationName(null);
                },
                onError: () => {
                    clearTimeout(optimisticUpdate);
                    setDeletingIntegrationName(null);
                    showNotification(t('integrations.overview.notifications.deleteError'), 'error');
                },
            }
        );
    };

    const showSkeleton = isExchangeCreating || isWalletCreating || isCsvImporting || isSyncing || isLoading;
    const stepValidation = validateStep1ToStep2();
    const accountState = useUnifiedAccountState();
    
    const handleGuidanceAction = () => {
        if (accountState.guidance?.actionRoute) {
            navigate(accountState.guidance.actionRoute);
        }
    };

    return (
        <PageLayout>
            <IntegrationsOverview
                integrations={integrations}
                onSync={handleSync}
                onDelete={handleDelete}
                deletingIntegrationName={deletingIntegrationName}
                isLoading={showSkeleton}
            />
        </PageLayout>
    );
};

const Integrations: React.FC = () => {
    return (
        <Routes>
            <Route index element={<IntegrationsMain />} />
            <Route path="add" element={<IntegrationSelectPage />} />
            <Route path="add/:platformName" element={<IntegrationCreatePage />} />
            <Route path=":integrationId" element={<IntegrationDetailPage />} />
        </Routes>
    );
};

export default Integrations;
